import { KeyboardEvent, MouseEventHandler, useCallback, useRef } from "react";

import { TextProvider } from "../../../context/Text";
import { TypographyProvider, useTypography } from "../../../context/Typography";
import { useContentColor } from "../../../styles/colors/content/useContentColor";
import { ContentEmphasis, KeyColorRole } from "../../../styles/colors/types";
import {
  typographyBodyLineHeightMap,
  TypographyBodySize,
  typographyBodySizeMap,
  TypographyLineHeight
} from "../../../styles/typography/typography";
import { FontWeight } from "../../../types";
import { Box } from "../../layout";
import getLinkStyles from "../utils/getLinkStyles";

type Props = {
  children: React.ReactNode | string;
  onClick: MouseEventHandler;
  disabled?: boolean;
  size?: TypographyBodySize;
  lineHeight?: TypographyLineHeight;
  tone?: KeyColorRole;
  emphasis?: ContentEmphasis;
  onHighEmphasisSurface?: boolean;
  disableInteractiveStyles?: boolean;
  underline?: boolean;
  fontWeight?: FontWeight;
};

export default function TextButton({
  children,
  onClick,
  disabled = false,
  size: sizeProp,
  lineHeight: lineHeightProp,
  tone = "primary",
  emphasis = "high",
  onHighEmphasisSurface = false,
  disableInteractiveStyles = false,
  underline = false,
  fontWeight = "bold"
}: Props) {
  const buttonRef = useRef<HTMLElement>();

  const color = useContentColor({ tone, emphasis, onHighEmphasisSurface });

  const styleProps = getLinkStyles(
    disabled,
    disableInteractiveStyles,
    underline,
    fontWeight
  );

  const {
    typographyBodySize: typographyBodySizeFromContext,
    lineHeight: lineHeightFromContext
  } = useTypography();

  const size = sizeProp || typographyBodySizeFromContext || "medium";
  const lineHeight = lineHeightProp || lineHeightFromContext || "small";

  const onKeyDown = useCallback((event: KeyboardEvent) => {
    if (buttonRef.current && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      buttonRef.current.click();
    }
  }, []);

  return (
    <Box
      component="span"
      UNSAFE_role="button"
      tabindex={0}
      onClick={e => {
        !disabled && onClick(e);
      }}
      onKeyDown={onKeyDown}
      ref={buttonRef}
      {...typographyBodySizeMap[size]}
      {...typographyBodyLineHeightMap[lineHeight]}
      color={color}
      backgroundColor="transparent"
      borderColor="transparent"
      cursor="pointer"
      {...styleProps}
    >
      <TextProvider value>
        <TypographyProvider value={{ typographyBodySize: size }}>
          {children}
        </TypographyProvider>
      </TextProvider>
    </Box>
  );
}
